// src/modules/time-tracking/components/ShiftTrackMap.js
// Renders a shift's GPS day on a Google map: a polyline of pings, stop markers
// (green=known location, red=unexpected), start/latest markers, and a geofence
// circle per segment anchor. Reuses the app's robust Maps loader hook.
import React, { useEffect, useRef } from 'react';
import useGoogleMapsLoader from '../../leads/route-planner/useGoogleMapsLoader';

const n = (v) => (v === null || v === undefined ? null : Number(v));

const ShiftTrackMap = ({ pings = [], stops = [], segments = [], height = '440px' }) => {
  const { isLoaded, error } = useGoogleMapsLoader();
  const ref = useRef(null);

  useEffect(() => {
    if (!isLoaded || !ref.current || !window.google?.maps) return;
    const gm = window.google.maps;

    const track = pings
      .map((p) => ({ lat: n(p.latitude), lng: n(p.longitude) }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));

    const firstSeg = segments.find((s) => s.anchor_lat != null);
    const center = track[0]
      || (firstSeg ? { lat: n(firstSeg.anchor_lat), lng: n(firstSeg.anchor_lng) } : { lat: 42.2626, lng: -71.8023 });

    const map = new gm.Map(ref.current, {
      zoom: 14, center, mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
    });
    const bounds = new gm.LatLngBounds();

    // Geofence circles for each segment anchor.
    segments.forEach((s) => {
      if (s.anchor_lat == null) return;
      const c = { lat: n(s.anchor_lat), lng: n(s.anchor_lng) };
      new gm.Circle({
        map, center: c, radius: (s.radius_feet || 600) * 0.3048,
        strokeColor: '#F69620', strokeOpacity: 0.6, strokeWeight: 1,
        fillColor: '#F69620', fillOpacity: 0.08,
      });
      bounds.extend(c);
    });

    // Track polyline.
    if (track.length > 1) {
      new gm.Polyline({
        map, path: track, geodesic: true,
        strokeColor: '#3498db', strokeOpacity: 0.9, strokeWeight: 3,
      });
    }
    track.forEach((p) => bounds.extend(p));

    if (track.length) {
      new gm.Marker({ map, position: track[0], label: 'A', title: 'First fix' });
      new gm.Marker({ map, position: track[track.length - 1], label: 'B', title: 'Latest fix' });
    }

    // Stop markers.
    const iw = new gm.InfoWindow();
    stops.forEach((st) => {
      if (st.latitude == null) return;
      const pos = { lat: n(st.latitude), lng: n(st.longitude) };
      const marker = new gm.Marker({
        map, position: pos, title: 'Stop',
        icon: {
          path: gm.SymbolPath.CIRCLE, scale: 7,
          fillColor: st.is_known_location ? '#84c959' : '#e74c3c',
          fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2,
        },
      });
      const mins = Math.round((st.duration_seconds || 0) / 60);
      marker.addListener('click', () => {
        iw.setContent(
          `<div style="color:#000;font-size:13px;max-width:220px">
             <b>Stop &middot; ${mins} min</b><br/>
             ${st.address ? st.address : 'Unknown location'}<br/>
             <span style="color:${st.is_known_location ? '#2e7d32' : '#c62828'}">
               ${st.is_known_location ? 'Known location' : 'Off-site'}
             </span>
           </div>`
        );
        iw.open(map, marker);
      });
      bounds.extend(pos);
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds);
      // Avoid over-zoom for a single-point day.
      const l = gm.event.addListenerOnce(map, 'idle', () => {
        if (map.getZoom() > 17) map.setZoom(17);
      });
      return () => gm.event.removeListener(l);
    }
  }, [isLoaded, pings, stops, segments]);

  if (error) return <div style={{ padding: 16, color: '#FF5252' }}>Map failed to load. {error}</div>;
  if (!isLoaded) return <div style={{ padding: 16, color: '#CCCCCC' }}>Loading map…</div>;
  return <div ref={ref} style={{ width: '100%', height, borderRadius: 8, overflow: 'hidden' }} />;
};

export default ShiftTrackMap;
